import React, { useState } from 'react'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { IContextMenuState } from 'types'
import Button from 'components/button'
import Menu from 'components/menu'
import { MenuFooter, MenuItem, MenuList } from 'components/menu/items'
import Tooltip from 'components/tooltip'
import { popoverPlacement } from 'components/popover'
import { Options } from 'components/icons'

export interface RowAction {
  label: string
  /** An icon before the label in the menu (not shown when the action stands alone). */
  icon?: React.ReactNode
  onSelect: () => void
  /** Red, for actions that delete or remove. */
  destructive?: boolean
  /** Under way: the action shows a spinner and can't be chosen again. */
  loading?: boolean
  disabled?: boolean
  /** Why it can't be chosen, on hover. */
  disabledReason?: string
  'data-tour'?: string
}

const MENU_WIDTH = 200
const MENU_ROW_HEIGHT = 32

/**
 * A single action as an underlined word, quiet enough to repeat down every row of a table, in the body
 * colour with an underline that darkens when pointed at.
 */
export const RowActionLink: React.FC<{ action: RowAction; name: string }> = ({ action, name }) => {
  const button = (
    <Button
      internalType="transparent"
      className="h-auto p-0 font-medium text-body underline underline-offset-2 decoration-body/40 hover:bg-transparent hover:text-body hover:decoration-body"
      aria-label={`${action.label} ${name}`}
      data-tour={action['data-tour']}
      isLoading={action.loading}
      disabled={action.disabled}
      onClick={action.onSelect}
    >
      {action.label}
    </Button>
  )
  return action.disabled && action.disabledReason ? (
    <Tooltip content={action.disabledReason}>
      <span className="inline-flex">{button}</span>
    </Tooltip>
  ) : (
    button
  )
}

/**
 * What can be done to a row, at its end: one action as an underlined word, or several (or one, with
 * `menu`, as for a destructive one that shouldn't sit in the row) behind a "⋯" button that opens a menu of
 * them, so a column of controls doesn't crowd the rows. `name` is the row's
 * name, for screen readers: "Remove Ada", "Actions for Ada".
 */
const RowActions: React.FC<{
  name: string
  actions: RowAction[]
  menu?: boolean
  /** A note at the foot of the menu, as for why an action can't be chosen. */
  footer?: React.ReactNode
}> = ({ name, actions, menu: alwaysMenu = false, footer }) => {
  const [menu, setMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  if (actions.length === 0) return null
  if (actions.length === 1 && !alwaysMenu) return <RowActionLink action={actions[0]} name={name} />

  return (
    <>
      <Tooltip content="Actions">
        <Button
          internalType="transparent"
          aria-label={`Actions for ${name}`}
          aria-haspopup="menu"
          aria-expanded={menu.open}
          className="-mr-2 size-7 p-0 text-body"
          // The menu closes on a press outside it, and this button counts as outside: while it's open,
          // keep the press from reaching the menu and let the click close it instead.
          onMouseDown={(event: React.MouseEvent) => {
            if (menu.open) event.stopPropagation()
          }}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            if (menu.open) return setMenu(INITIAL_CONTEXT_MENU_STATE)
            setMenu(
              popoverPlacement(event.currentTarget.getBoundingClientRect(), {
                width: MENU_WIDTH,
                height: actions.length * MENU_ROW_HEIGHT + 8
              })
            )
          }}
        >
          <Options />
        </Button>
      </Tooltip>
      {menu.open && (
        <Menu id={`row-actions-${name}`} menuState={menu} setMenuState={setMenu} width={MENU_WIDTH} fixed>
          <MenuList>
            {actions.map((action) => (
              <MenuItem
                key={action.label}
                icon={action.icon}
                variant={action.destructive ? 'destructive' : 'default'}
                loading={action.loading}
                disabled={action.disabled || action.loading}
                disabledReason={action.disabledReason}
                data-tour={action['data-tour']}
                onClick={() => {
                  setMenu(INITIAL_CONTEXT_MENU_STATE)
                  action.onSelect()
                }}
              >
                {action.label}
              </MenuItem>
            ))}
          </MenuList>
          {footer && <MenuFooter>{footer}</MenuFooter>}
        </Menu>
      )}
    </>
  )
}

export default RowActions
